import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {formatDate} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useMemo, useRef, useState} from 'react'
import {useGetInfiniteProjectsByPatient} from '@/features/projects/queries/useGetInfiniteProjectsByPatient'
import {useInputSearch} from '@/hooks/useInputSearch'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {ProjectByPatient} from '@/features/projects/types'

interface InputSelectProjectProps {
    patientId: string
    disabled?: boolean
    label?: string
    value?: Pick<ProjectByPatient, 'id' | 'code' | 'startDate' | 'expirationDate'> | null
    onChange: (value: ProjectByPatient | null) => void
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
    disableClosedProjects?: boolean
}

export const InputSelectProjectByPatient = ({
    patientId,
    disabled,
    label,
    onChange,
    errorMessage,
    placeholder,
    onClear,
    value,
    disableClosedProjects
}: InputSelectProjectProps) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const inputProjectRef = useRef(null)
    const {searchValue, onSearch, onResetSearch} = useInputSearch()
    const {
        remappedData: projects,
        isLoading,
        isError,
        hasNextPage,
        fetchNextPage
    } = useGetInfiniteProjectsByPatient({
        urlParams: {patientId},
        params: {
            search: searchValue,
            limit: 10
        },
        options: {enabled: !disabled && isQueryEnabled}
    })

    const projectName = useMemo(
        () =>
            value?.startDate && value?.expirationDate && value.code
                ? t('medical_records:code_date_expiration', {
                      startDate: formatDate(value.startDate, 'DD/MM/YYYY'),
                      expirationDate: formatDate(value.expirationDate, 'DD/MM/YYYY'),
                      code: value.code
                  })
                : value?.code
                  ? t('projects:pri_n', {code: value?.code})
                  : t('commons:select'),
        [value?.code, value?.startDate, value?.expirationDate]
    )

    return (
        <ComboboxProvider setValue={onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.id ?? ''}
                setValue={value => onChange(projects.find(project => project && project.id == value) ?? null)}
                setOpen={isOpen => {
                    onResetSearch(inputProjectRef)
                    setIsQueryEnabled(isOpen)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    {label && <Label>{label}</Label>}
                    <SelectTrigger
                        placeholder={placeholder}
                        fullWidth
                        value={value?.id}
                        displayValue={() => (
                            <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                {projectName}
                                {onClear && (
                                    <XCloseIcon
                                        role="button"
                                        onClick={e => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            onClear()
                                        }}
                                    />
                                )}
                            </Flexbox>
                        )}
                        hasError={!!errorMessage}
                        disabled={disabled}
                    />

                    {errorMessage && (
                        <Flexbox align="center" gap={1.5}>
                            <InputStatusIcon hasError={!!errorMessage} />
                            <InputHelpText error={errorMessage} />
                        </Flexbox>
                    )}

                    <SelectComboboxPopover>
                        <SelectComboboxInput ref={inputProjectRef} placeholder={t('commons:search_placeholder')} />
                        <SelectComboboxList>
                            {isLoading ? (
                                Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                            ) : isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : projects.length > 0 ? (
                                <SelectVirtualList
                                    data={projects}
                                    totalCount={projects.length}
                                    overscan={100}
                                    endReached={() => hasNextPage && fetchNextPage()}
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem
                                            disabled={disableClosedProjects && item.status == 'closed'}
                                            hasItemCheckbox
                                            key={itemIndex}
                                            value={item.id}
                                        >
                                            {item.startDate && item.expirationDate && item.code
                                                ? t('medical_records:code_date_expiration', {
                                                      startDate:
                                                          (item.startDate &&
                                                              formatDate(item.startDate, 'DD/MM/YYYY')) ||
                                                          '',
                                                      expirationDate:
                                                          (item.expirationDate &&
                                                              formatDate(item.expirationDate, 'DD/MM/YYYY')) ||
                                                          '',
                                                      code: item.code
                                                  })
                                                : item.code && t('projects:pri_n', {code: item.code})}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
